<script setup>
// "How much is left on my voucher?" - the form behind the Check Voucher Balance section.
//
// The journey is the same one the header's Redeem bar runs, up to the point where that one
// puts the voucher on the basket: a code can need a PIN, or need the customer to say which
// partner issued it, and either step can follow the other. All of that lives in the shared
// flow, which opens those steps in the pop-out panel. What is left here is the answer.
//
// Deliberately renders the result in place rather than in a panel. A balance is something a
// customer reads, checks against what they wanted to book, and often comes back to - a panel
// they have to dismiss would take it away at the moment it becomes useful.
import { computed, ref } from 'vue';
import { createVoucherFlow } from '@/shared/voucherFlow.js';
import { redeemOutcome } from '@/shared/redeemOutcome.js';
import { formatPrice } from '@/shared/formatPrice.js';

const props = defineProps({
    checkUrl: { type: String, required: true },
    // The header Redeem bar's own endpoint, for the Redeem button on a result. Published by
    // the section rather than taken from the search-bar widget, which not every page carries.
    redeemUrl: { type: String, required: true },
});

const code = ref('');

// The last answer, or null before one arrives. Both a balance and an expired voucher land
// here: they are the two outcomes that answer the question rather than asking another.
const result = ref(null);

const flow = createVoucherFlow({
    url: props.checkUrl,
    onOutcome(data, { end }) {
        if (data.outcome === 'balance' || data.outcome === 'expired') {
            result.value = data;
            // Closes the PIN or picker panel if the journey took a second step, so the
            // answer is not hidden behind it.
            end();

            return true;
        }

        return false;
    },
});

const state = flow.state;

// Redeeming is a second journey rather than a continuation of this one, so it gets its own
// flow: a different endpoint, and its own busy and error state. Sharing one would have the
// Redeem button's progress blank out the balance the customer is reading.
//
// It reuses redemption's own outcome handler, so this button finishes exactly where the
// header Redeem bar does - the basket panel when the voucher already covers something
// chosen, the entitled-product page when it does not.
const redeem = createVoucherFlow({
    url: props.redeemUrl,
    onOutcome: redeemOutcome,
});

const balance = computed(() => formatPrice(result.value?.balance));

// Only where there is money to spend. A partner voucher entitling the holder to one fixed
// package has no balance, and redemption from here would be a different promise - it is the
// cash card whose holder is standing at "£42.50, now what?".
const canRedeem = computed(() => result.value?.outcome === 'balance' && result.value?.balance > 0);

function redeemNow() {
    redeem.submit({ code: result.value.code });
}

function submit() {
    const value = code.value.trim();

    if (! value) {
        return;
    }

    // Clear the previous answer up front: leaving it on screen while a different code is
    // being checked invites reading the old voucher's balance as the new one's.
    result.value = null;

    flow.submit({ code: value });
}
</script>

<template>
    <div class="check_balance">
        <form class="check_balance_form" @submit.prevent="submit">
            <div class="field">
                <label for="balance_voucher_code">Your voucher number</label>
                <input
                    id="balance_voucher_code"
                    v-model="code"
                    type="text"
                    name="voucher_code"
                    placeholder="Enter your voucher number..."
                    autocomplete="off"
                    autocapitalize="characters"
                    spellcheck="false"
                >
            </div>

            <button type="submit" class="button teal" :disabled="state.busy">
                {{ state.busy ? 'Checking...' : 'Check Balance' }}
            </button>
        </form>

        <!-- Shown only when no step panel is open, which is where the flow puts its errors
            while one is. -->
        <p v-if="state.error && ! state.panelOpen" class="form_error" role="alert">
            {{ state.error }}
        </p>

        <div v-if="result" class="check_balance_result" role="status">
            <p class="check_balance_code">
                Voucher <strong>{{ result.code }}</strong>
                <template v-if="result.provider"> from <strong>{{ result.provider.name }}</strong></template>
            </p>

            <template v-if="result.outcome === 'expired'">
                <p class="check_balance_expiry">{{ result.message }}</p>
            </template>

            <template v-else>
                <!-- A cash voucher leads with its balance; a partner voucher leads with what
                    it entitles the holder to, since its value is not money they can spend
                    elsewhere. Some carry both, which is why neither is a v-else of the other. -->
                <p v-if="balance" class="check_balance_amount">
                    Balance <strong>{{ balance }}</strong>
                </p>

                <template v-if="result.items?.length">
                    <p class="check_balance_entitlement">This voucher can be used for:</p>
                    <ul class="check_balance_items">
                        <li v-for="item in result.items" :key="item">{{ item }}</li>
                    </ul>
                </template>

                <p v-if="result.expiresAt" class="check_balance_expiry">
                    Expires {{ result.expiresAt }}
                </p>

                <!-- The whole point of this warning is that the figure above is
                     provisional: the spa does not take the money off until the customer
                     arrives, so a code already committed elsewhere still reports it. -->
                <div v-if="result.previouslyUsed" class="voucher_used_notice">
                    <p>
                        <strong>Important:</strong> This voucher has already been used to
                        secure another booking. Please make sure it has enough balance to
                        cover both when you arrive at your appointment.
                    </p>
                </div>

                <!-- Takes the customer through exactly where the header's Redeem bar would,
                    so the voucher is not actually spent until they complete checkout. -->
                <div v-if="canRedeem" class="check_balance_actions">
                    <button
                        type="button"
                        class="button teal"
                        :disabled="redeem.state.busy"
                        @click="redeemNow"
                    >
                        {{ redeem.state.busy ? 'Just a moment...' : 'Redeem Now' }}
                    </button>

                    <!-- The redeem journey's own errors, kept apart from the lookup's so a
                        failure here cannot be read as a problem with the balance above. -->
                    <p
                        v-if="redeem.state.error && ! redeem.state.panelOpen"
                        class="form_error"
                        role="alert"
                    >
                        {{ redeem.state.error }}
                    </p>
                </div>
            </template>
        </div>
    </div>
</template>
